import { csvRow } from "@/lib/csv";
import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Download, Mail, Search, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

type Subscriber = {
  id: string;
  email: string;
  marketing_consent: boolean;
  created_at: string;
};

const PAGE_SIZE = 25;

const AdminSubscribers = () => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const queryClient = useQueryClient();

  const handleDelete = async (id: string, email: string) => {
    if (!confirm(`Remove ${email} from subscribers?`)) return;
    const { error } = await supabase.from("subscribers" as any).delete().eq("id", id);
    if (error) {
      toast.error("Failed to delete subscriber");
    } else {
      toast.success(`${email} removed`);
      queryClient.invalidateQueries({ queryKey: ["admin-subscribers"] });
    }
  };

  const { data: subscribers = [], isLoading } = useQuery({
    queryKey: ["admin-subscribers", search],
    queryFn: async () => {
      let query = supabase
        .from("subscribers" as any)
        .select("*")
        .order("created_at", { ascending: false });

      if (search) {
        query = query.ilike("email", `%${search}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as unknown as Subscriber[];
    },
  });

  const exportCSV = () => {
    if (!subscribers.length) {
      toast.error("No subscribers to export");
      return;
    }

    const header = "Email,Marketing Consent,Signed Up";
    const rows = subscribers.map(
      (s) =>
        csvRow([s.email, s.marketing_consent ? "Yes" : "No", new Date(s.created_at).toLocaleDateString()])
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${subscribers.length} subscribers`);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold">Subscribers</h1>
          <p className="text-sm text-muted-foreground font-body">
            {subscribers.length} email subscribers
          </p>
        </div>
        <Button onClick={exportCSV} variant="outline" size="sm" className="gap-2">
          <Download className="w-4 h-4" />
          Export CSV
        </Button>
      </div>

      <div className="relative mb-6 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search by email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-12 bg-secondary animate-pulse rounded" />
          ))}
        </div>
      ) : subscribers.length === 0 ? (
        <div className="text-center py-16">
          <Mail className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-muted-foreground font-body">No subscribers yet</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm font-body">
            <thead>
              <tr className="border-b border-border bg-secondary/50">
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Email</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Marketing</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Signed Up</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const totalPages = Math.max(1, Math.ceil(subscribers.length / PAGE_SIZE));
                const safePage = Math.min(page, totalPages - 1);
                const paged = subscribers.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);
                return (
                  <>
                    {paged.map((sub) => (
                      <tr key={sub.id} className="border-b border-border/50 last:border-0 hover:bg-secondary/30 transition-colors">
                        <td className="px-4 py-3">{sub.email}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${sub.marketing_consent ? "bg-green-100 text-green-700" : "bg-secondary text-muted-foreground"}`}>
                            {sub.marketing_consent ? "Yes" : "No"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{new Date(sub.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</td>
                        <td className="px-4 py-3">
                          <button onClick={() => handleDelete(sub.id, sub.email)} className="text-muted-foreground hover:text-destructive transition-colors" title="Delete subscriber">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </>
                );
              })()}
            </tbody>
          </table>
          {subscribers.length > PAGE_SIZE && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border">
              <p className="text-sm text-muted-foreground font-body">Page {Math.min(page, Math.ceil(subscribers.length / PAGE_SIZE) - 1) + 1} of {Math.ceil(subscribers.length / PAGE_SIZE)}</p>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" disabled={page === 0} onClick={() => setPage(page - 1)}>Prev</Button>
                <Button size="sm" variant="ghost" disabled={page >= Math.ceil(subscribers.length / PAGE_SIZE) - 1} onClick={() => setPage(page + 1)}>Next</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminSubscribers;